/**
 * The interpreter's Tools (web): one panel for everything the interpreter
 * does in a call, opened from the Tools key on the call bar and minimized
 * back to it. It docks beside the video (the video narrows, nothing covers
 * the Deaf client), or on a narrow window sits over the bottom of the
 * screen, capped so the client's video stays visible above it.
 *
 * Sections, in order (sections.ts, shared with the Terp sheet): Phone (VRS
 * calls), Transfer and Team (when they apply), Add participant, Shift. The
 * panel stays mounted while minimized, so a typed number, a pending transfer
 * or an uncertain break change survive it.
 */
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import Icon from '../../../base/icons/components/Icon';
import { IconArrowDown } from '../../../base/icons/svg';
import PhoneSection from '../../../interpreter-phone/components/PhoneSection.web';
import InterpreterStatusToggle from '../../../interpreter-queue/components/web/InterpreterStatusToggle';
import ShiftSection from '../../../vrs-layout/components/ShiftSection.web';
import { setInterpreterToolsMounted, setInterpreterToolsOpen } from '../../actions.web';
import { activeToolsCall } from '../../activeCall';
import { INTERPRETER_TOOLS_DOCK_MIN_WIDTH, INTERPRETER_TOOLS_PANEL_ID, INTERPRETER_TOOLS_PANEL_WIDTH } from '../../constants';
import { interpreterToolSections, isPhoneLive } from '../../sections';
import { useTeamTool } from '../../team';
import AddParticipantSection from './AddParticipantSection';
import TeamSection from './TeamSection';
import TransferSection from './TransferSection';
const useStyles = makeStyles()(theme => {
    return {
        panel: {
            background: theme.palette.ui01,
            borderLeft: `1px solid ${theme.palette.ui03}`,
            boxSizing: 'border-box',
            color: theme.palette.text01,
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
            height: '100%',
            minHeight: 0,
            position: 'relative',
            width: INTERPRETER_TOOLS_PANEL_WIDTH,
            zIndex: 300,
            // Author display rules beat the hidden attribute's own.
            '&[hidden]': {
                display: 'none'
            },
            [`@media (max-width: ${INTERPRETER_TOOLS_DOCK_MIN_WIDTH - 1}px)`]: {
                borderLeft: 0,
                borderRadius: '16px 16px 0 0',
                borderTop: `1px solid ${theme.palette.ui03}`,
                bottom: 0,
                height: 'auto',
                left: 0,
                maxHeight: '50dvh',
                position: 'fixed',
                right: 0,
                width: 'auto',
                zIndex: 351
            }
        },
        header: {
            alignItems: 'center',
            borderBottom: `1px solid ${theme.palette.ui03}`,
            display: 'flex',
            flexShrink: 0,
            justifyContent: 'space-between',
            minHeight: 56,
            padding: '0 8px 0 16px'
        },
        title: {
            fontSize: 16,
            fontWeight: 700,
            lineHeight: '24px',
            margin: 0,
            outline: 0
        },
        minimize: {
            alignItems: 'center',
            background: 'transparent',
            border: 0,
            borderRadius: 8,
            color: theme.palette.text01,
            cursor: 'pointer',
            display: 'inline-flex',
            height: 44,
            justifyContent: 'center',
            width: 44,
            '&:hover': {
                background: theme.palette.ui03
            },
            '&:focus-visible': {
                outline: `2px solid ${theme.palette.focus01}`,
                outlineOffset: -2
            }
        },
        body: {
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: '0 16px 24px'
        },
        section: {
            borderBottom: `1px solid ${theme.palette.ui03}`,
            padding: '12px 0 16px',
            '&:last-of-type': {
                borderBottom: 0
            }
        },
        sectionTitle: {
            color: theme.palette.text02,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.06em',
            lineHeight: '16px',
            margin: '0 0 8px',
            textTransform: 'uppercase'
        }
    };
});
const SECTION_TITLES = {
    addParticipant: 'vrs.tools.sections.addParticipant',
    phone: 'vrs.tools.sections.phone',
    shift: 'vrs.tools.sections.shift',
    team: 'vrs.tools.sections.team',
    transfer: 'vrs.tools.sections.transfer'
};
/**
 * The interpreter's Tools panel.
 *
 * @param {IProps} props - The props.
 * @returns {ReactElement}
 */
export default function InterpreterToolsPanel({ callType, phone, vriRoom }) {
    const { t } = useTranslation();
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const open = useSelector((state) => Boolean(state['features/interpreter-tools']?.open));
    const room = useSelector((state) => state['features/base/conference'].room);
    const matchData = useSelector((state) => state['features/interpreter-queue']?.matchData);
    const activeCall = useMemo(() => activeToolsCall(room, matchData), [room, matchData]);
    const callId = activeCall?.callId;
    const team = useTeamTool(callId, activeCall?.team);
    const sections = interpreterToolSections({
        callId,
        callType: callType || activeCall?.callType,
        phoneLive: isPhoneLive({
            hangupPending: phone.hangupPending,
            status: phone.callStatus
        }),
        teamApplies: team.applies,
        teammate: team.teammate,
        vriRoom
    });
    const heading = useRef(null);
    const openedFor = useRef('');
    useEffect(() => {
        dispatch(setInterpreterToolsMounted(true));
        return () => {
            dispatch(setInterpreterToolsMounted(false));
        };
    }, [dispatch]);
    // A call that starts with the client's hearing number opens on Phone,
    // ready to dial, once per number; a VCO call opens once for its voice
    // controls.
    const openFor = (phone.callStatus === 'idle' && phone.initialPhoneNumber?.trim())
        || (phone.vcoEnabled && phone.vcoRoomName ? `vco:${phone.vcoRoomName}` : '');
    useEffect(() => {
        if (openFor && openFor !== openedFor.current) {
            openedFor.current = openFor;
            dispatch(setInterpreterToolsOpen(true));
        }
    }, [dispatch, openFor]);
    // A failed phone call is never hidden behind the minimized key.
    useEffect(() => {
        if (phone.callStatus === 'failed') {
            dispatch(setInterpreterToolsOpen(true));
        }
    }, [dispatch, phone.callStatus]);
    // Opened from the key: move focus into the panel.
    useEffect(() => {
        if (open && typeof document !== 'undefined'
            && document.activeElement?.closest?.('[data-interpreter-tools-key]')) {
            heading.current?.focus();
        }
    }, [open]);
    const minimize = useCallback(() => {
        dispatch(setInterpreterToolsOpen(false));
        document.querySelector('[data-interpreter-tools-key]')?.focus();
    }, [dispatch]);
    const onKeyDown = useCallback((event) => {
        if (event.key === 'Escape') {
            event.stopPropagation();
            minimize();
        }
    }, [minimize]);
    const renderSection = (section) => {
        switch (section) {
            case 'phone':
                return React.createElement(PhoneSection, { ...phone });
            case 'transfer':
                return (React.createElement(TransferSection, { callId: callId, open: open }));
            case 'team':
                return callId && (React.createElement(TeamSection, { callId: callId, open: open, team: team }));
            case 'addParticipant':
                return callId && React.createElement(AddParticipantSection, { callId: callId });
            case 'shift':
                return (React.createElement(React.Fragment, null,
                    React.createElement(ShiftSection, { open: open }),
                    React.createElement(InterpreterStatusToggle, { docked: true })));
        }
    };
    return (React.createElement("aside", { "aria-label": t('vrs.tools.title'), className: classes.panel, "data-tool-sections": sections.join(' '), hidden: !open, id: INTERPRETER_TOOLS_PANEL_ID, onKeyDown: onKeyDown },
        React.createElement("div", { className: classes.header },
            React.createElement("h2", { className: classes.title, ref: heading, tabIndex: -1 }, t('vrs.tools.title')),
            React.createElement("button", { "aria-label": t('vrs.tools.minimize'), className: classes.minimize, onClick: minimize, type: 'button' },
                React.createElement(Icon, { size: 20, src: IconArrowDown }))),
        React.createElement("div", { className: classes.body }, sections.map(section => (React.createElement("section", { "aria-labelledby": `interpreter-tools-${section}`, className: classes.section, "data-tool-section": section, key: section },
            React.createElement("h3", { className: classes.sectionTitle, id: `interpreter-tools-${section}` }, t(SECTION_TITLES[section])),
            renderSection(section)))))));
}
